import React, {useEffect, useRef, useState} from "react";
import {Button, Modal} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faAlignCenter,
  faAlignJustify,
  faAlignLeft,
  faAlignRight,
  faBold,
  faItalic,
  faQuestionCircle,
  faTable,
  faUnderline
} from "@fortawesome/free-solid-svg-icons";
import {ToastContainer} from "react-toastify";
import AppComponentCardHeader from "../../common/AppComponentCardHeader";
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import {toastError, toastSuccess} from "../../../../helpers/Toast";
import {contractContentToEditorHtml, editorHtmlToContractContent} from "../../../../helpers/ContractRichText";
import "../../../../css/DictionaryForms.css";
import "../../../../css/ContractTemplates.css";

const emptyTemplate = {
  id: null,
  name: "",
  description: "",
  contractTypeId: "",
  content: "",
  active: false
};

const fontSizes = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28];

const contractPlaceholders = [
  {value: "CONTRACT_PLACE", label: "Miejscowość zawarcia umowy"},
  {value: "CONTRACT_DATE", label: "Data zawarcia umowy"},
  {value: "COMPANY_REPRESENTATIVE", label: "Reprezentant firmy"},
  {value: "POSITION", label: "Stanowisko"},
  {value: "WORK_PLACE", label: "Miejsce wykonywania pracy"},
  {value: "WORKING_TIME", label: "Wymiar czasu pracy"},
  {value: "WORKING_TIME_PERCENTAGE", label: "Wymiar czasu pracy (%)"},
  {value: "EMPLOYMENT_CONDITIONS", label: "Warunki zatrudnienia"},
  {value: "WORK_START_DATE", label: "Data rozpoczęcia pracy"}
];

const wagePlaceholders = [
  {value: "HOURLY_RATE", label: "Stawka godzinowa brutto"},
  {value: "HOURLY_RATE_WORDS", label: "Stawka godzinowa brutto słownie"},
  {value: "NET_HOURLY_RATE", label: "Stawka godzinowa netto"},
  {value: "NET_HOURLY_RATE_WORDS", label: "Stawka godzinowa netto słownie"},
  {value: "MONTHLY_RATE", label: "Wynagrodzenie miesięczne brutto"},
  {value: "MONTHLY_RATE_WORDS", label: "Wynagrodzenie miesięczne brutto słownie"},
  {value: "NET_MONTHLY_RATE", label: "Wynagrodzenie miesięczne netto"},
  {value: "NET_MONTHLY_RATE_WORDS", label: "Wynagrodzenie miesięczne netto słownie"}
];

const wordsFunctionPlaceholders = [
  {value: "HOURLY_RATE", label: "Stawka godzinowa brutto słownie"},
  {value: "NET_HOURLY_RATE", label: "Stawka godzinowa netto słownie (gdy brak netto: brutto)"},
  {value: "MONTHLY_RATE", label: "Wynagrodzenie miesięczne brutto słownie"},
  {value: "NET_MONTHLY_RATE", label: "Wynagrodzenie miesięczne netto słownie (gdy brak netto: brutto)"},
  {value: "WORKING_TIME_PERCENTAGE", label: "Wymiar czasu pracy słownie"},
  {value: "CONTRACT_SEQUENCE_NUMBER", label: "Numer kolejny słownie"},
  {value: "CONTRACT_YEAR", label: "Rok słownie"},
  {value: "CONTRACT_MONTH", label: "Miesiąc słownie"}
];

const ContractTemplate = () => {
  const axiosPrivate = useAxiosPrivate();
  const editorRef = useRef(null);
  const savedRangeRef = useRef(null);
  const [items, setItems] = useState([]);
  const [contractTypes, setContractTypes] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [current, setCurrent] = useState(emptyTemplate);
  const [editing, setEditing] = useState(false);
  const [fontSize, setFontSize] = useState("10");
  const [saving, setSaving] = useState(false);
  const [showTableInsert, setShowTableInsert] = useState(false);
  const [tableRows, setTableRows] = useState(2);
  const [tableColumns, setTableColumns] = useState(2);

  const load = async () => {
    try {
      const [templatesResponse, typesResponse, placeholdersResponse] = await Promise.all([
        axiosPrivate.get("/contract-templates"),
        axiosPrivate.get("/contract-types"),
        axiosPrivate.get("/contract-templates/placeholders")
      ]);
      setItems(templatesResponse.data || []);
      setContractTypes(typesResponse.data || []);
      setPlaceholders(placeholdersResponse.data || []);
    } catch (e) {
      toastError("Nie udało się pobrać szablonów treści umów");
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!editing || !editorRef.current) return;
    editorRef.current.innerHTML = contractContentToEditorHtml(current.content);
    savedRangeRef.current = null;
  }, [editing, current.id]);

  const edit = item => {
    setCurrent({...item, contractTypeId: item.contractTypeId || ""});
    setEditing(true);
  };

  const add = () => {
    setCurrent({...emptyTemplate});
    setEditing(true);
  };

  const close = () => {
    setEditing(false);
    setCurrent({...emptyTemplate});
    savedRangeRef.current = null;
    setShowTableInsert(false);
  };

  const getEditorText = () => editorRef.current?.innerText?.replace(/\u00a0/g, " ").trim() || "";
  const getEditorContent = () => editorHtmlToContractContent(editorRef.current?.innerHTML || "");

  const save = async () => {
    const content = getEditorContent();
    if (!current.name.trim() || !getEditorText()) {
      toastError("Nazwa i treść szablonu są wymagane");
      return;
    }
    if (current.active && !current.contractTypeId) {
      toastError("Automatyczny szablon musi być przypisany do typu umowy");
      return;
    }

    const payload = {
      ...current,
      content,
      contractTypeId: current.contractTypeId ? Number(current.contractTypeId) : null
    };

    try {
      setSaving(true);
      const response = current.id
        ? await axiosPrivate.put(`/contract-templates/${current.id}`, payload)
        : await axiosPrivate.post("/contract-templates", payload);
      const saved = response.data || payload;
      setCurrent({...saved, contractTypeId: saved.contractTypeId || ""});
      await load();
      toastSuccess("Szablon umowy został zapisany");
    } catch (e) {
      toastError(typeof e?.response?.data === "string" ? e.response.data : e?.response?.data?.message || "Nie udało się zapisać szablonu umowy");
    } finally {
      setSaving(false);
    }
  };

  const remove = async id => {
    try {
      await axiosPrivate.delete(`/contract-templates/${id}`);
      if (current.id === id) close();
      await load();
    } catch (e) {
      toastError("Nie udało się usunąć szablonu umowy");
    }
  };

  const saveSelection = () => {
    const selection = window.getSelection();
    if (!selection?.rangeCount || !editorRef.current) return;
    const range = selection.getRangeAt(0);
    if (editorRef.current.contains(range.commonAncestorContainer)) {
      savedRangeRef.current = range.cloneRange();
    }
  };

  const restoreSelection = () => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    if (!savedRangeRef.current) return;
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(savedRangeRef.current);
  };

  const execute = command => {
    restoreSelection();
    document.execCommand("styleWithCSS", false, true);
    document.execCommand(command, false, null);
    saveSelection();
  };

  const changeFontSize = size => {
    setFontSize(size);
    restoreSelection();
    document.execCommand("styleWithCSS", false, false);
    document.execCommand("fontSize", false, "7");
    editorRef.current?.querySelectorAll('font[size="7"]').forEach(font => {
      const span = document.createElement("span");
      span.style.fontSize = `${size}pt`;
      while (font.firstChild) span.appendChild(font.firstChild);
      font.replaceWith(span);
    });
    document.execCommand("styleWithCSS", false, true);
    saveSelection();
  };

  const insertPlaceholder = value => {
    restoreSelection();
    document.execCommand("insertText", false, `{{${value}}}`);
    saveSelection();
  };

  const insertWordsFunction = value => {
    restoreSelection();
    document.execCommand("insertText", false, `{{SLOWNIE(${value})}}`);
    saveSelection();
  };

  const insertTable = () => {
    const rows = Math.max(1, Math.min(20, Number(tableRows) || 1));
    const columns = Math.max(1, Math.min(12, Number(tableColumns) || 1));
    const cells = Array.from({length: columns}, () => '<td style="border: 1px solid #000; padding: 4px; vertical-align: top"><br></td>').join("");
    const html = `<table style="border-collapse: collapse; width: 100%"><tbody>${Array.from({length: rows}, () => `<tr>${cells}</tr>`).join("")}</tbody></table><div><br></div>`;
    restoreSelection();
    document.execCommand("insertHTML", false, html);
    saveSelection();
    setShowTableInsert(false);
  };

  const toolbarButton = (icon, title, command) => (
    <button
      type="button"
      className="btn btn-sm btn-outline-secondary contract-editor-tool"
      title={title}
      onMouseDown={e => {
        e.preventDefault();
        execute(command);
      }}
    >
      <FontAwesomeIcon icon={icon}/>
    </button>
  );

  const featuredPlaceholders = [...contractPlaceholders, ...wagePlaceholders];
  const remainingPlaceholders = placeholders.filter(value => !featuredPlaceholders.some(item => item.value === value));

  return (
    <div id="layoutSidenav_content" className="settings-entity-page contract-template-page">
      <ToastContainer/>
      <AppComponentCardHeader title="Szablony treści umów"/>
      <div className="container-fluid px-4 py-3">
        <div className="alert alert-info py-2">
          Szablon treści odpowiada za wygląd i zawartość pliku DOCX. Numer, dane pracownika i wynagrodzenie są podstawiane z zapisanej umowy przez pola dynamiczne.
        </div>

        <div className="card shadow-sm bg-white rounded-2 p-3 mb-3">
          <div className="d-flex justify-content-between align-items-center gap-3 mb-3">
            <div>
              <strong>Szablony dokumentów</strong>
              <div className="text-muted small">Dla jednego typu umowy może istnieć wiele szablonów, ale tylko jeden może być wybierany automatycznie.</div>
            </div>
            <Button variant="primary" onClick={add}>Dodaj szablon</Button>
          </div>
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
              <thead><tr><th>Nazwa</th><th>Typ umowy</th><th>Automatyczny</th><th></th></tr></thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.id}>
                    <td>{item.name}</td>
                    <td>{item.contractTypeName || "Uniwersalny / tylko ręczny wybór"}</td>
                    <td>{item.active ? "TAK" : "NIE"}</td>
                    <td className="text-end text-nowrap">
                      <Button size="sm" variant="outline-primary" className="me-2" onClick={() => edit(item)}>Edytuj</Button>
                      <Button size="sm" variant="outline-danger" onClick={() => remove(item.id)}>Usuń</Button>
                    </td>
                  </tr>
                ))}
                {!items.length && <tr><td colSpan="4" className="text-muted">Brak zdefiniowanych szablonów.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Modal
        show={editing}
        onHide={close}
        backdrop="static"
        keyboard={false}
        size="xl"
        centered
        dialogClassName="contract-template-editor-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{current.id ? `Edycja wzoru: ${current.name}` : "Nowy wzór treści umowy"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="contract-template-section">
            <div className="contract-template-section-title">Ustawienia wzoru</div>
            <div className="row g-3 align-items-end">
              <div className="col-lg-5">
                <label className="form-label">Nazwa</label>
                <input className="form-control" value={current.name} onChange={e => setCurrent({...current, name: e.target.value})}/>
              </div>
              <div className="col-lg-5">
                <label className="form-label">Typ umowy</label>
                <select className="form-select" value={current.contractTypeId || ""} onChange={e => setCurrent({...current, contractTypeId: e.target.value, active: e.target.value ? current.active : false})}>
                  <option value="">Uniwersalny / wybierany ręcznie</option>
                  {contractTypes.map(type => <option key={type.id} value={type.id}>{type.name}</option>)}
                </select>
              </div>
              <div className="col-lg-2">
                <div className="d-flex align-items-center gap-2 pb-2">
                  <input
                    id="contract-template-active"
                    type="checkbox"
                    className="form-check-input mt-0"
                    checked={!!current.active}
                    disabled={!current.contractTypeId}
                    onChange={e => setCurrent({...current, active: e.target.checked})}
                  />
                  <label htmlFor="contract-template-active" className="form-check-label">Automatyczny</label>
                  <div className="form_tooltip m-0">
                    <FontAwesomeIcon icon={faQuestionCircle}/>
                    <span className="form_tooltip_text">
                      Po zaznaczeniu ten wzór jest domyślnie używany przy generowaniu DOCX dla wybranego typu umowy. Dla jednego typu umowy może być aktywny tylko jeden wzór.
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-12">
                <label className="form-label">Opis</label>
                <input className="form-control" value={current.description || ""} onChange={e => setCurrent({...current, description: e.target.value})}/>
              </div>
            </div>
          </div>

          <div className="contract-template-section">
            <div className="contract-template-section-title">Dane umowy w treści dokumentu</div>
            <div className="small text-muted mb-2">
              Te wartości są pobierane z zapisanej umowy. Jeżeli pole nie zostało uzupełnione, część danych może zostać uzupełniona z konfiguracji firmy lub danych pracownika.
            </div>
            <div className="contract-placeholder-list contract-placeholder-list-expanded">
              {contractPlaceholders.map(item => (
                <Button key={item.value} type="button" size="sm" variant="outline-primary" onMouseDown={e => { e.preventDefault(); insertPlaceholder(item.value); }}>
                  {item.label} {`{{${item.value}}}`}
                </Button>
              ))}
            </div>
          </div>

          <div className="contract-template-section">
            <div className="contract-template-section-title">Wynagrodzenie w treści umowy</div>
            <div className="small text-muted mb-2">
              Wartość stawki wpisujesz przy konkretnej umowie pracownika. Tutaj wybierasz miejsce, w którym ma się pojawić w dokumencie.
            </div>
            <div className="d-flex flex-wrap gap-2">
              {wagePlaceholders.map(item => (
                <Button key={item.value} type="button" size="sm" variant="outline-primary" onMouseDown={e => { e.preventDefault(); insertPlaceholder(item.value); }}>
                  {item.label} {`{{${item.value}}}`}
                </Button>
              ))}
            </div>
          </div>

          <div className="contract-template-section">
            <div className="contract-template-section-title">Liczby słownie</div>
            <div className="small text-muted mb-2">
              Funkcja SLOWNIE pobiera wartość liczbową z umowy i zapisuje ją po polsku. Dla stawek wynagrodzenia uwzględnia złote i grosze.
            </div>
            <div className="d-flex flex-wrap gap-2">
              {wordsFunctionPlaceholders.map(item => (
                <Button key={item.value} type="button" size="sm" variant="outline-primary" onMouseDown={e => { e.preventDefault(); insertWordsFunction(item.value); }}>
                  {item.label} {`{{SLOWNIE(${item.value})}}`}
                </Button>
              ))}
            </div>
          </div>

          <div className="contract-template-section mb-0">
            <div className="contract-template-section-title">Treść umowy</div>
            <div className="contract-editor-toolbar">
              <div className="btn-group" role="group">
                {toolbarButton(faBold, "Pogrubienie", "bold")}
                {toolbarButton(faItalic, "Kursywa", "italic")}
                {toolbarButton(faUnderline, "Podkreślenie", "underline")}
              </div>
              <div className="btn-group" role="group">
                {toolbarButton(faAlignLeft, "Wyrównaj do lewej", "justifyLeft")}
                {toolbarButton(faAlignCenter, "Wyśrodkuj", "justifyCenter")}
                {toolbarButton(faAlignRight, "Wyrównaj do prawej", "justifyRight")}
                {toolbarButton(faAlignJustify, "Wyjustuj", "justifyFull")}
              </div>
              <select
                className="form-select form-select-sm contract-editor-font-size"
                value={fontSize}
                onMouseDown={saveSelection}
                onChange={e => changeFontSize(e.target.value)}
                title="Rozmiar czcionki"
              >
                {fontSizes.map(size => <option key={size} value={size}>{size} pt</option>)}
              </select>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary contract-editor-tool"
                title="Wstaw tabelę"
                onMouseDown={e => {
                  e.preventDefault();
                  saveSelection();
                  setShowTableInsert(value => !value);
                }}
              >
                <FontAwesomeIcon icon={faTable}/>
              </button>
              {showTableInsert && (
                <div className="contract-table-insert">
                  <label>Wiersze
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={tableRows}
                      onMouseDown={saveSelection}
                      onChange={e => setTableRows(e.target.value)}
                    />
                  </label>
                  <span>×</span>
                  <label>Kolumny
                    <input
                      type="number"
                      min="1"
                      max="12"
                      value={tableColumns}
                      onMouseDown={saveSelection}
                      onChange={e => setTableColumns(e.target.value)}
                    />
                  </label>
                  <Button
                    type="button"
                    size="sm"
                    variant="primary"
                    onMouseDown={e => {
                      e.preventDefault();
                      insertTable();
                    }}
                  >
                    Wstaw
                  </Button>
                </div>
              )}
            </div>

            <div
              ref={editorRef}
              className="form-control contract-rich-editor"
              contentEditable
              suppressContentEditableWarning
              onMouseUp={saveSelection}
              onKeyUp={saveSelection}
              onInput={saveSelection}
            />

            <div className="mt-3">
              <div className="small fw-semibold mb-2">Pozostałe pola dynamiczne</div>
              <div className="contract-placeholder-list">
                {remainingPlaceholders.map(value => (
                  <button key={value} type="button" className="btn btn-sm btn-outline-secondary" onMouseDown={e => { e.preventDefault(); insertPlaceholder(value); }}>
                    {`{{${value}}}`}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={close}>Zamknij</Button>
          <Button variant="primary" onClick={save} disabled={saving}>{saving ? "Zapisywanie..." : "Zapisz"}</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ContractTemplate;

